/**
 * Landing-page tracking for the borrower-paid launch email "Set it up now" CTA.
 * Email clients cannot call Statsig; we append campaign query params on the deep
 * link and log once after the user lands in the LOS (post-login if needed).
 *
 * Management strips query via history.replace — remember the landing search in
 * sessionStorage so a late Statsig client init can still report the event.
 */
import * as com from '../Common'

export const BORROWER_PAID_LAUNCH_CAMPAIGN = 'borrower_paid_launch'
export const BORROWER_PAID_LAUNCH_EVENT = 'borrower_paid_launch_email_cta_click'

const PENDING_SEARCH_KEY = 'bp_launch_pending_search'
const PENDING_PATH_KEY = 'bp_launch_pending_path'

export function isBorrowerPaidLaunchCtaLanding(search = window.location.search) {
    const params = new URLSearchParams(search || '')
    return (
        params.get('utm_campaign') === BORROWER_PAID_LAUNCH_CAMPAIGN ||
        params.get('bp_cta') === '1'
    )
}

/** Call before any history.replace that would drop campaign query params. */
export function rememberBorrowerPaidLaunchLanding(search = window.location.search) {
    if (!isBorrowerPaidLaunchCtaLanding(search)) {
        return false
    }
    sessionStorage.setItem(PENDING_SEARCH_KEY, search)
    sessionStorage.setItem(PENDING_PATH_KEY, window.location.pathname)
    return true
}

/**
 * Log the email CTA open once per landing URL in this browser session.
 * Safe to call from Management / Settings mount; no-ops without Statsig client
 * or without campaign query params (current URL or remembered pending).
 */
export function trackBorrowerPaidLaunchCtaLanding(statsigClient, extras = {}) {
    try {
        const currentSearch = window.location.search
        const pendingSearch = sessionStorage.getItem(PENDING_SEARCH_KEY) || ''
        const search = isBorrowerPaidLaunchCtaLanding(currentSearch)
            ? currentSearch
            : pendingSearch

        if (!isBorrowerPaidLaunchCtaLanding(search)) {
            return false
        }

        // Keep pending until the client is ready (Management may have already
        // stripped the visible query string).
        if (!statsigClient) {
            rememberBorrowerPaidLaunchLanding(search)
            return false
        }

        const path =
            (isBorrowerPaidLaunchCtaLanding(currentSearch)
                ? window.location.pathname
                : sessionStorage.getItem(PENDING_PATH_KEY)) || window.location.pathname
        const landingKey = path + search
        const storageKey = 'bp_launch_cta_tracked:' + landingKey
        if (sessionStorage.getItem(storageKey) === '1') {
            sessionStorage.removeItem(PENDING_SEARCH_KEY)
            sessionStorage.removeItem(PENDING_PATH_KEY)
            return false
        }

        const params = new URLSearchParams(search)
        const customerid = com.getCustomerId() || ''
        let email = ''
        try {
            const saved = JSON.parse(sessionStorage.getItem('statsig_user_info') || '{}')
            email = (saved.email || saved.custom?.email || '').toLowerCase().trim()
        } catch (e) {
            /* ignore */
        }

        statsigClient.logEvent(BORROWER_PAID_LAUNCH_EVENT, customerid, {
            operation_method: 'email_cta_landing',
            item_name: BORROWER_PAID_LAUNCH_EVENT,
            utm_source: params.get('utm_source') || 'email',
            utm_campaign: params.get('utm_campaign') || BORROWER_PAID_LAUNCH_CAMPAIGN,
            utm_content: params.get('utm_content') || '',
            landing_path: path,
            select: params.get('select') || '',
            creditTab: params.get('creditTab') || params.get('tab') || '',
            customerid,
            email,
            timestamp: Date.now(),
            ...extras,
        })
        sessionStorage.setItem(storageKey, '1')
        sessionStorage.removeItem(PENDING_SEARCH_KEY)
        sessionStorage.removeItem(PENDING_PATH_KEY)
        return true
    } catch (e) {
        console.warn('trackBorrowerPaidLaunchCtaLanding', e)
        return false
    }
}
